import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Sentry from '@sentry/react';
import type { GroupId } from 'balus-common';
import { trpcClient } from '@framework/trpcClient';

type Props = {
  groupId: GroupId;
  className?: string;
};

type RequestState = 'idle' | 'sending' | 'sent' | 'failed';

/**
 * 上限到達の案内と、上限の引き上げ依頼をSlackへ送るボタン。
 * AIアシスタントの入力欄と利用量ダイアログの両方に出すため切り出している。
 *
 * 連打の抑止は送信済みフラグをRTDBに持たず、このボタンの無効化だけで行う。
 * 別のタブや再読み込みからは再送できるが、通知先は運用チャネルなので重複は許容する。
 */
export const AIUsageLimitReachedNotice: React.FC<Props> = ({ groupId, className }: Props) => {
  const { t } = useTranslation();
  const [requestState, setRequestState] = useState<RequestState>('idle');

  const handleRequest = useCallback(async () => {
    setRequestState('sending');
    try {
      await trpcClient.group.notifyAIUsageLimitReached.mutate({ groupId });
      setRequestState('sent');
    } catch (error) {
      Sentry.captureException(error);
      setRequestState('failed');
    }
  }, [groupId]);

  return (
    <div className={`rounded border border-red-700 bg-red-100 p-2 text-xs text-red-800 ${className ?? ''}`}>
      <p>{t('framework.aiUsage.overLimitMessage')}</p>
      <div className="mt-2 flex items-center gap-2">
        <button
          type="button"
          className="rounded bg-red-700 px-2 py-1 font-bold text-white hover:bg-red-800 disabled:bg-gray-400"
          disabled={requestState === 'sending' || requestState === 'sent'}
          onClick={handleRequest}
        >
          {t('framework.aiUsage.limitReachedRequestButton')}
        </button>
        {requestState === 'sent' && <span>{t('framework.aiUsage.limitReachedRequestSent')}</span>}
        {requestState === 'failed' && <span>{t('framework.aiUsage.limitReachedRequestFailed')}</span>}
      </div>
    </div>
  );
};
